import type { VenueStorefrontBookingSummaryExtra } from '../venue-storefront-booking-summary-card/venue-storefront-booking-summary-card.component'
import { BookingProductRefundPolicyComponent } from '../venue-storefront-booking-summary-card/booking-product-refund-policy.component'
import type { FormSubmissionStatus, PaymentFormStatus } from '../../../utilities/form-status'
import { CurrencyPipe, DatePipe, I18nPluralPipe, NgTemplateOutlet } from '@angular/common'
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    ViewChild,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidatorFn,
    Validators,
} from '@angular/forms'
import { RxwebValidators } from '@rxweb/reactive-form-validators'
import {
    ChangeData,
    CountryISO,
    NgxIntlTelInputModule,
    PhoneNumberFormat,
    SearchCountryField,
} from 'ngx-intl-tel-input-gg'
import { QuillViewComponent } from 'ngx-quill'
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap'
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs'

import type { Answer } from '../../../domain/Answer'
import { answerForQuestion } from '../../../domain/Answer'
import type { BookingPaymentRequirement } from '../../../domain/BookingSlotOption'
import { bookingPaymentTerminologyForVenueId } from '../../../domain/BookingPaymentTerminology'
import { Question, QuestionType } from '../../../domain/Question'
import type { Venue } from '../../../domain/Venue'
import { CapitaliseOnBlurDirective } from '../../../directives/capitalise-on-blur.directive'
import { VenueColourDirective } from '../../../directives/venue-colour.directive'
import { MultipleChoiceInputComponent } from '../../../multiple-choice-input/multiple-choice-input.component'
import { CurrencyUnitPipe } from '../../../pipes/currency-unit.pipe'
import type { PaymentUsage } from '../../../services/PaymentSession'
import type { PaymentIntent } from '../../../domain/PaymentIntent'
import { environment } from '../../../../environments/environment'
import { richTextPlainText } from '../../../utilities/rich-text'
import type { VenueStorefrontBookingDetails } from '../venue-storefront-booking-flow/venue-storefront-booking-flow.types'
import { VenueStorefrontBookingPaymentPlaceholderComponent } from '../venue-storefront-booking-payment-placeholder/venue-storefront-booking-payment-placeholder.component'

const MONTH_FORMATTER = new Intl.DateTimeFormat('en-GB', { month: 'long' })
import { VenueStorefrontBookingPaymentElementComponent } from '../venue-storefront-booking-payment-element/venue-storefront-booking-payment-element.component'
import { BookingPhoneAccessibilityDirective } from './booking-phone-accessibility.directive'

type BookingQuestionAnswer = string | boolean | null

type BookingQuestionForm = FormGroup<{
    question: FormControl<Question>
    answer: FormControl<BookingQuestionAnswer>
}>

@Component({
    selector: 'app-venue-storefront-booking-details-step',
    standalone: true,
    imports: [
        BookingPhoneAccessibilityDirective,
        BookingProductRefundPolicyComponent,
        CapitaliseOnBlurDirective,
        CurrencyUnitPipe,
        DatePipe,
        I18nPluralPipe,
        MultipleChoiceInputComponent,
        NgbDropdownModule,
        NgTemplateOutlet,
        NgxIntlTelInputModule,
        QuillViewComponent,
        ReactiveFormsModule,
        VenueColourDirective,
        VenueStorefrontBookingPaymentElementComponent,
        VenueStorefrontBookingPaymentPlaceholderComponent,
    ],
    providers: [CurrencyPipe],
    templateUrl: './venue-storefront-booking-details-step.component.html',
    styleUrls: ['./venue-storefront-booking-details-step.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenueStorefrontBookingDetailsStepComponent implements AfterViewInit {
    venue = input.required<Venue>()
    initialDetails = input<VenueStorefrontBookingDetails | null>(null)
    reasonId = input.required<string | null>()
    basketReasonIds = input<string[]>([])
    eventId = input.required<string | null>()
    partySize = input.required<number>()
    bookingEndTime = input.required<Date>()
    upsellItems = input<VenueStorefrontBookingSummaryExtra[]>([])
    paymentRequirement = input.required<BookingPaymentRequirement | null>()
    paymentIntent = input.required<PaymentIntent | null>()
    paymentUsage = input.required<PaymentUsage | null>()
    isPreparingPayment = input.required<boolean>()
    showPaymentSection = input.required<boolean>()
    paymentRequirementResolved = input.required<boolean>()
    isSubmitting = input.required<boolean>()
    errorMessage = input.required<string | null>()
    bookingSlotUnavailable = input.required<boolean>()
    emailValidationFailed = input.required<boolean>()

    submissionStatusChange = output<FormSubmissionStatus>()
    paymentStatusChange = output<PaymentFormStatus>()
    paymentPreparationRequested = output<VenueStorefrontBookingDetails>()
    detailsChanged = output<VenueStorefrontBookingDetails>()
    detailsSubmitted = output<VenueStorefrontBookingDetails>()
    paymentConfirmed = output<string>()
    paymentFinalisingChange = output<boolean>()

    @ViewChild('heading', { static: true }) private heading!: ElementRef<HTMLHeadingElement>
    @ViewChild('detailsForm', { static: true }) private detailsForm!: ElementRef<HTMLFormElement>
    @ViewChild(VenueStorefrontBookingPaymentElementComponent)
    private paymentElement?: VenueStorefrontBookingPaymentElementComponent

    protected readonly QuestionType = QuestionType
    protected readonly CountryISO = CountryISO
    protected readonly SearchCountryField = SearchCountryField
    protected readonly PhoneNumberFormat = PhoneNumberFormat
    protected readonly hoursPluralMapping = {
        '=1': '# hour',
        other: '# hours',
    }
    protected readonly privacyPolicyURL = environment.privacyPolicyURL
    protected readonly merchantPrivacyPolicyPath = computed(() => {
        const venue = this.venue()
        return venue.venueSlug && richTextPlainText(venue.privacyPolicy)
            ? `/discover/${encodeURIComponent(venue.venueSlug)}/booking-policy#privacy-policy-section`
            : null
    })
    protected readonly months = Array.from({ length: 12 }, (_, month) => {
        return MONTH_FORMATTER.format(new Date(2000, month, 1))
    })
    protected readonly days = Array.from({ length: 31 }, (_, index) => index + 1)
    protected readonly form = new FormGroup({
        firstName: new FormControl('', {
            nonNullable: true,
            validators: [Validators.required],
        }),
        lastName: new FormControl('', {
            nonNullable: true,
            validators: [Validators.required],
        }),
        emailAddress: new FormControl('', {
            nonNullable: true,
            validators: [Validators.required, RxwebValidators.email()],
        }),
        phoneNumber: new FormControl<ChangeData | null>(null),
        notes: new FormControl<string | null>(null),
        questions: new FormArray<BookingQuestionForm>([]),
        agreedToMarketing: new FormControl(true, { nonNullable: true }),
        agreedToMerchantPrivacy: new FormControl(false, { nonNullable: true }),
        agreedToTerms: new FormControl(false, {
            nonNullable: true,
            validators: [Validators.requiredTrue],
        }),
    })
    protected readonly paymentReady = signal(false)
    protected readonly paymentConfirming = signal(false)
    protected readonly formValid = signal(false)
    protected readonly phoneNumberCountryCode = computed(() => {
        return this.venue().address.getISO3166CountryCode().toLowerCase() as CountryISO
    })
    protected readonly paymentTerminology = computed(() => {
        const payment = this.paymentRequirement()
        return bookingPaymentTerminologyForVenueId(this.venue().id, payment?.kind === 'deposit' && payment.isPaidUpsell)
    })
    protected readonly futureChargeAmount = computed(() => {
        const payment = this.paymentRequirement()
        if (payment?.kind !== 'card') {
            return null
        }
        return payment.amount * (payment.isPerCover ? this.partySize() : 1)
    })
    protected readonly submitDisabled = computed(() => {
        if (
            this.isPreparingPayment()
            || this.isSubmitting()
            || this.paymentConfirming()
            || this.paymentPreparationFailed()
            || this.bookingSlotUnavailable()
        ) {
            return true
        }
        if (!this.formValid()) {
            return false
        }
        if (this.showPaymentSection() && !this.paymentRequirementResolved()) {
            return true
        }
        return this.paymentIntent() !== null && !this.paymentReady()
    })
    protected readonly paymentPreparationFailed = computed(() => {
        return this.showPaymentSection()
            && this.errorMessage() !== null
            && !this.bookingSlotUnavailable()
    })
    protected readonly submitLabel = computed(() => {
        if (this.isSubmitting()) {
            return 'Requesting booking…'
        }
        if (this.paymentConfirming()) {
            return this.paymentUsage() === 'payment' ? 'Taking payment…' : 'Securing booking…'
        }
        if (this.paymentIntent() !== null) {
            return this.paymentUsage() === 'payment' ? 'Pay & Book' : 'Confirm & Book'
        }
        return this.paymentRequirement()?.kind === 'deposit' ? 'Pay & Book' : 'Confirm & Book'
    })

    private readonly changeDetectorRef = inject(ChangeDetectorRef)

    private lastPaymentIntentSecret: string | null = null

    private readonly questionsEffect = effect(() => {
        const reasonIds = this.basketReasonIds()
        const questions = reasonIds.length > 0
            ? [...new Map(reasonIds.flatMap(id => this.venue().questionsToAskWhenBookingForConfiguration(id, null))
                .map(question => [question.id, question])).values()]
            : this.venue().questionsToAskWhenBookingForConfiguration(this.reasonId(), this.eventId())
        untracked(() => this.replaceQuestions(questions))
    })

    private readonly phoneValidatorEffect = effect(() => {
        const phoneNumberRequired = this.venue().phoneNumberRequired
        untracked(() => {
            if (phoneNumberRequired) {
                this.form.controls.phoneNumber.addValidators(Validators.required)
            } else {
                this.form.controls.phoneNumber.removeValidators(Validators.required)
            }
            this.form.controls.phoneNumber.updateValueAndValidity({ emitEvent: false })
        })
    })

    private readonly formStateEffect = effect(() => {
        const frozen = this.isSubmitting()
            || (this.basketReasonIds().length > 1 && this.isPreparingPayment())
            || this.paymentConfirming()
            || this.bookingSlotUnavailable()
        const emailValidationFailed = this.emailValidationFailed()
        untracked(() => {
            if (frozen && this.form.enabled) {
                this.form.disable({ emitEvent: false })
            } else if (!frozen && this.form.disabled) {
                this.form.enable({ emitEvent: false })
            }
            if (!frozen && emailValidationFailed) {
                const email = this.form.controls.emailAddress
                email.setErrors({ ...(email.errors ?? {}), serverValidation: true })
                email.markAsTouched()
            }
        })
    })

    private readonly paymentSecretEffect = effect(() => {
        const clientSecret = this.paymentIntent()?.clientSecret ?? null
        untracked(() => {
            if (clientSecret !== this.lastPaymentIntentSecret) {
                this.lastPaymentIntentSecret = clientSecret
                this.paymentReady.set(false)
                this.paymentConfirming.set(false)
            }
        })
    })

    constructor() {
        this.form.statusChanges
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.formValid.set(this.form.valid))
        this.form.controls.emailAddress.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.removeServerEmailValidationError())
        this.form.valueChanges
            .pipe(
                map(() => this.currentDetails()),
                distinctUntilChanged((previous, current) => {
                    return JSON.stringify(previous) === JSON.stringify(current)
                }),
                tap(details => this.detailsChanged.emit(details)),
                debounceTime(300),
                takeUntilDestroyed()
            )
            .subscribe(() => this.requestPaymentPreparation())
    }

    ngAfterViewInit(): void {
        this.heading.nativeElement.focus({ preventScroll: true })
        const details = this.initialDetails()
        if (details !== null) {
            queueMicrotask(() => this.restoreInitialDetails(details))
        }
    }

    private restoreInitialDetails(details: VenueStorefrontBookingDetails): void {
        this.form.patchValue({
            ...details,
            phoneNumber: details.phoneNumber
                ? { number: details.phoneNumber, e164Number: details.phoneNumber }
                : null,
        }, { emitEvent: false })
        for (const control of this.questions.controls) {
            const question = control.controls.question.value
            const answer = details.answers.find(value => value.question.id === question.id)
            if (answer) {
                control.controls.answer.setValue(
                    question.isTickable ? answer.answer === 'true' : answer.answer,
                    { emitEvent: false }
                )
            }
        }
        this.formValid.set(this.form.valid)
        this.changeDetectorRef.markForCheck()
    }

    protected get questions(): FormArray<BookingQuestionForm> {
        return this.form.controls.questions
    }

    protected submit(): void {
        this.form.markAllAsTouched()
        if (this.form.invalid) {
            this.submissionStatusChange.emit('validation_failed')
            this.focusFirstInvalidControl()
            return
        }
        if (this.submitDisabled()) {
            return
        }

        if (this.paymentIntent() !== null) {
            void this.paymentElement?.confirm()
            return
        }

        if (this.showPaymentSection() && !this.paymentRequirementResolved()) {
            this.requestPaymentPreparation()
            return
        }
        this.detailsSubmitted.emit(this.currentDetails())
    }

    protected requestPaymentPreparation(): void {
        if (
            !this.showPaymentSection()
            || this.paymentRequirementResolved()
            || this.paymentIntent() !== null
            || this.isPreparingPayment()
            || this.isSubmitting()
            || this.bookingSlotUnavailable()
            || this.form.invalid
        ) {
            return
        }
        this.paymentPreparationRequested.emit(this.currentDetails())
    }

    private currentDetails(): VenueStorefrontBookingDetails {
        const details = this.form.getRawValue()
        return {
            firstName: details.firstName,
            lastName: details.lastName,
            emailAddress: details.emailAddress,
            phoneNumber: details.phoneNumber?.e164Number ?? null,
            notes: details.notes,
            answers: this.questions.controls
                .map(control => answerForQuestion(
                    control.controls.question.value,
                    control.controls.answer.value
                ))
                .filter((answer): answer is Answer => answer !== null),
            agreedToMarketing: details.agreedToMarketing,
            agreedToTerms: details.agreedToTerms,
            ...(this.merchantPrivacyPolicyPath() ? { agreedToMerchantPrivacy: details.agreedToMerchantPrivacy } : {}),
        }
    }

    protected isInvalid(control: AbstractControl): boolean {
        return control.invalid && control.touched
    }

    protected questionIsInvalid(control: BookingQuestionForm): boolean {
        return this.isInvalid(control.controls.answer)
    }

    protected questionLabel(question: Question): string {
        if (question.type === QuestionType.DietaryRestrictions
            || question.type === QuestionType.DietaryRestrictionsText) {
            return question.text || 'Dietary restrictions'
        }
        return question.text
    }

    protected dobMonth(control: BookingQuestionForm): string {
        return this.dobParts(control)[0] ?? ''
    }

    protected dobDay(control: BookingQuestionForm): string {
        return this.dobParts(control)[1] ?? ''
    }

    protected updateDob(
        control: BookingQuestionForm,
        part: 'month' | 'day',
        event: Event
    ): void {
        const value = (event.target as HTMLSelectElement).value
        let [month = '', day = ''] = this.dobParts(control)
        if (part === 'month') {
            month = value
            if (month === '') {
                day = ''
            }
        } else {
            day = value
        }
        control.controls.answer.setValue(month === '' && day === '' ? null : `${month}/${day}`)
        control.controls.answer.markAsTouched()
    }

    protected dietarySelections(control: BookingQuestionForm): string[] {
        const value = control.controls.answer.value
        return typeof value === 'string' && value.length > 0 ? value.split(',') : []
    }

    protected paymentConfirmedFromElement(intentId: string): void {
        this.paymentConfirmed.emit(intentId)
    }

    protected paymentConfirmingChanged(isConfirming: boolean): void {
        this.paymentConfirming.set(isConfirming)
        this.paymentFinalisingChange.emit(isConfirming)
    }

    private replaceQuestions(questions: Question[]): void {
        this.questions.clear({ emitEvent: false })
        questions.forEach(question => {
            this.questions.push(new FormGroup({
                question: new FormControl(question, { nonNullable: true }),
                answer: new FormControl<BookingQuestionAnswer>(
                    question.isTickable ? false : null,
                    this.validatorsForQuestion(question)
                ),
            }), { emitEvent: false })
        })
    }

    private validatorsForQuestion(question: Question): ValidatorFn[] {
        const validators: ValidatorFn[] = []
        if (question.required) {
            validators.push(question.isTickable ? Validators.requiredTrue : Validators.required)
        }
        if (question.type === QuestionType.DOB) {
            validators.push(control => this.validateDayOfYear(control.value))
        }
        return validators
    }

    private validateDayOfYear(value: unknown): Record<string, true> | null {
        if (value === null || value === '') {
            return null
        }
        if (typeof value !== 'string') {
            return { invalidDate: true }
        }
        const [monthValue, dayValue] = value.split('/')
        if (monthValue && !dayValue) {
            return { dayRequired: true }
        }
        const month = Number(monthValue)
        const day = Number(dayValue)
        if (!Number.isInteger(month) || month < 1 || month > 12
            || !Number.isInteger(day) || day < 1) {
            return { invalidDate: true }
        }
        const maximumDay = new Date(2020, month, 0).getDate()
        return day <= maximumDay ? null : { invalidDate: true }
    }

    private dobParts(control: BookingQuestionForm): string[] {
        const value = control.controls.answer.value
        return typeof value === 'string' ? value.split('/') : []
    }

    private removeServerEmailValidationError(): void {
        const email = this.form.controls.emailAddress
        if (!email.hasError('serverValidation')) {
            return
        }
        const { serverValidation: _serverValidation, ...remainingErrors } = email.errors ?? {}
        email.setErrors(Object.keys(remainingErrors).length > 0 ? remainingErrors : null)
    }

    private focusFirstInvalidControl(): void {
        queueMicrotask(() => {
            const invalidControl = this.detailsForm.nativeElement.querySelector<HTMLElement>(
                ':invalid, [aria-invalid="true"]'
            )
            invalidControl?.focus()
        })
    }
}
